import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Inbox, Send, FileText, Trash2, AlertOctagon, RefreshCw, X, Reply,
         Paperclip, Search, Mail as MailIcon, Eye, ChevronLeft, Settings } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La sección Correo.
//
// No hay copia de los correos en la base: se habla directamente con el buzón de
// IONOS por IMAP. Lo que se ve aquí es lo mismo que en el móvil o en el webmail,
// y lo que se marca como leído aquí queda leído en todas partes. Es a propósito:
// una copia desincronizada es peor que no tener copia.

const ORO = '#c9a84c';

const ICONO = {
  entrada: Inbox, enviados: Send, borradores: FileText,
  papelera: Trash2, spam: AlertOctagon,
};

const cuando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  if (mismoDia) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const dias = Math.round((hoy - d) / 86400000);
  if (dias < 7) return d.toLocaleDateString('es-ES', { weekday: 'short' });
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const iniciales = (nombre, correo) => {
  const base = (nombre || correo || '?').trim();
  const p = base.split(/[\s@.]+/).filter(Boolean);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
};

// En el móvil no caben tres columnas (carpetas | lista | correo). Se enseña una
// cosa cada vez: las carpetas pasan a una tira horizontal arriba, y al abrir un
// correo ocupa toda la pantalla con un botón para volver.
function useEsMovil(limite = 820) {
  const [esMovil, setEsMovil] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= limite : false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${limite}px)`);
    const alCambiar = (e) => setEsMovil(e.matches);
    setEsMovil(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', alCambiar) : mq.addListener(alCambiar);
    return () => (mq.removeEventListener
      ? mq.removeEventListener('change', alCambiar) : mq.removeListener(alCambiar));
  }, [limite]);
  return esMovil;
}

export function SeccionMail({ orgId }) {
  const [estado, setEstado] = useState(null);
  const [carpetas, setCarpetas] = useState([]);
  const [carpeta, setCarpeta] = useState('entrada');
  const [mensajes, setMensajes] = useState([]);
  const [abierto, setAbierto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [buscando, setBuscando] = useState('');
  const [redactar, setRedactar] = useState(null); // {para,asunto,cuerpo,responder_a_uid}
  const [enviando, setEnviando] = useState(false);
  const [verPlantilla, setVerPlantilla] = useState(false);
  const busquedaRef = useRef(null);
  const esMovil = useEsMovil();

  const cargarCarpetas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mail/carpetas`);
      setCarpetas(data.carpetas || []);
    } catch (e) { /* el estado ya avisa si no hay configuración */ }
  }, []);

  const cargarMensajes = useCallback(async (car = carpeta, texto = '') => {
    setCargando(true);
    try {
      const q = new URLSearchParams({ carpeta: car, limite: '40' });
      if (texto) q.set('buscar', texto);
      const { data } = await axios.get(`${API}/mail/mensajes?${q}`);
      setMensajes(data.mensajes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo leer el buzón');
      setMensajes([]);
    } finally { setCargando(false); }
  }, [carpeta]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mail/estado`);
        setEstado(data);
        if (data.configurado) { await cargarCarpetas(); await cargarMensajes('entrada'); }
        else setCargando(false);
      } catch (e) { setCargando(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrir = async (m) => {
    try {
      const { data } = await axios.get(`${API}/mail/mensaje/${m.uid}?carpeta=${carpeta}`);
      setAbierto(data);
      if (!m.leido) {
        setMensajes((xs) => xs.map((x) => (x.uid === m.uid ? { ...x, leido: true } : x)));
        cargarCarpetas();
      }
    } catch (e) { toast.error('No se pudo abrir el correo'); }
  };

  const cambiarCarpeta = (alias) => {
    setCarpeta(alias); setAbierto(null); setBuscando('');
    cargarMensajes(alias);
  };

  const responder = (m) => {
    const de = m.de?.[0];
    setRedactar({
      para: de?.email || '',
      asunto: m.asunto?.startsWith('Re:') ? m.asunto : `Re: ${m.asunto}`,
      cuerpo: '',
      responder_a_uid: m.uid,
      responder_carpeta: carpeta,
    });
  };

  const enviar = async () => {
    if (!redactar?.para?.trim()) return toast.error('Falta el destinatario');
    if (!redactar?.asunto?.trim()) return toast.error('Falta el asunto');
    setEnviando(true);
    try {
      const cuerpoHtml = redactar.cuerpo
        .split(/\n{2,}/).map((p) => `<p style="margin:0 0 14px">${p.replace(/\n/g, '<br>')}</p>`).join('');
      const { data } = await axios.post(`${API}/mail/enviar`, {
        para: redactar.para.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        asunto: redactar.asunto,
        cuerpo: cuerpoHtml,
        con_plantilla: redactar.con_plantilla !== false,
        firmante: redactar.firmante || '',
        cargo: redactar.cargo || '',
        responder_a_uid: redactar.responder_a_uid || null,
        responder_carpeta: redactar.responder_carpeta || 'entrada',
      });
      toast.success(data.copia_en_enviados ? 'Enviado y guardado en Enviados' : 'Enviado');
      setRedactar(null);
      if (carpeta === 'enviados') cargarMensajes('enviados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally { setEnviando(false); }
  };

  const guardarBorrador = async () => {
    if (!redactar) return;
    try {
      const cuerpoHtml = `<p>${(redactar.cuerpo || '').replace(/\n/g, '<br>')}</p>`;
      await axios.post(`${API}/mail/borrador`, {
        para: (redactar.para || '').split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        asunto: redactar.asunto || '(sin asunto)',
        cuerpo: cuerpoHtml,
      });
      toast.success('Guardado en Borradores');
      setRedactar(null);
      cargarCarpetas();
    } catch (e) { toast.error('No se pudo guardar el borrador'); }
  };

  const mover = async (m, destino) => {
    try {
      await axios.post(`${API}/mail/${m.uid}/mover?carpeta=${carpeta}&destino=${destino}`);
      setMensajes((xs) => xs.filter((x) => x.uid !== m.uid));
      if (abierto?.uid === m.uid) setAbierto(null);
      toast.success(destino === 'papelera' ? 'A la papelera' : `Movido a ${destino}`);
      cargarCarpetas();
    } catch (e) { toast.error('No se pudo mover'); }
  };

  // ── Sin configurar ────────────────────────────────────────────────────────
  if (estado && !estado.configurado) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
        <Settings size={40} style={{ color: ORO, marginBottom: 14 }} />
        <h3 style={{ color: '#e2e8f0', margin: '0 0 8px', fontSize: 17 }}>El correo no está configurado</h3>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7 }}>
          Faltan <code style={{ color: ORO }}>MAIL_USER</code> y <code style={{ color: ORO }}>MAIL_PASS</code>
          {' '}en el <code style={{ color: ORO }}>.env</code> del backend.
        </p>
      </div>
    );
  }

  const sinLeer = (a) => carpetas.find((c) => c.alias === a)?.sin_leer || 0;

  // En el móvil se enseña una sola cosa: o la lista, o el correo abierto.
  const mostrarLista = !esMovil || !abierto;
  const mostrarLectura = !!abierto;

  return (
    <div style={{ display: 'flex', flexDirection: esMovil ? 'column' : 'row',
                  gap: esMovil ? 12 : 16, minHeight: esMovil ? 'auto' : 560 }}>

      {/* ── Carpetas: columna en escritorio, tira horizontal en el móvil ─── */}
      <div style={esMovil
        ? { width: '100%', display: abierto ? 'none' : 'block' }
        : { width: 190, flexShrink: 0 }}>
        <button
          onClick={() => setRedactar({ para: '', asunto: '', cuerpo: '', con_plantilla: true })}
          style={{ width: '100%', padding: '11px 14px', marginBottom: esMovil ? 10 : 14, background: ORO,
                   color: '#0a0a0a', border: 'none', borderRadius: 9, fontWeight: 700,
                   fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center',
                   justifyContent: 'center', gap: 7 }}>
          <Send size={15} /> Redactar
        </button>

        <div style={esMovil
          ? { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6,
              WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }
          : {}}>
          {carpetas.filter((c) => !c.alias.includes('/')).map((c) => {
            const Icono = ICONO[c.alias] || MailIcon;
            const activa = carpeta === c.alias;
            return (
              <button key={c.alias} onClick={() => cambiarCarpeta(c.alias)}
                style={esMovil
                  ? { flexShrink: 0, padding: '7px 13px', borderRadius: 20, fontSize: 12.5,
                      background: activa ? ORO : 'rgba(255,255,255,0.05)',
                      color: activa ? '#0a0a0a' : '#94a3b8', border: 'none', cursor: 'pointer',
                      fontWeight: activa ? 700 : 400, display: 'flex', alignItems: 'center', gap: 6,
                      textTransform: 'capitalize', whiteSpace: 'nowrap' }
                  : { width: '100%', padding: '9px 12px', marginBottom: 3, textAlign: 'left',
                      background: activa ? 'rgba(201,168,76,0.13)' : 'transparent',
                      color: activa ? ORO : '#94a3b8', border: 'none',
                      borderLeft: `2px solid ${activa ? ORO : 'transparent'}`,
                      borderRadius: '0 7px 7px 0', cursor: 'pointer', fontSize: 13,
                      display: 'flex', alignItems: 'center', gap: 9 }}>
                <Icono size={esMovil ? 13 : 15} />
                <span style={esMovil ? {} : { flex: 1, textTransform: 'capitalize' }}>{c.alias}</span>
                {c.sin_leer > 0 && (
                  <span style={{ background: esMovil ? '#0a0a0a' : ORO,
                                 color: esMovil ? ORO : '#0a0a0a', borderRadius: 9,
                                 padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>{c.sin_leer}</span>
                )}
              </button>
            );
          })}
          {esMovil && carpetas.filter((c) => c.alias.includes('/')).map((c) => (
            <button key={c.alias} onClick={() => cambiarCarpeta(c.alias)}
              style={{ flexShrink: 0, padding: '7px 13px', borderRadius: 20, fontSize: 12.5,
                       background: carpeta === c.alias ? ORO : 'rgba(255,255,255,0.05)',
                       color: carpeta === c.alias ? '#0a0a0a' : '#64748b',
                       border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {c.nombre}
            </button>
          ))}
        </div>

        {!esMovil && carpetas.some((c) => c.alias.includes('/')) && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
            <p style={{ margin: '0 0 7px 12px', fontSize: 10, letterSpacing: 1.2,
                        color: '#475569', textTransform: 'uppercase' }}>Etiquetas</p>
            {carpetas.filter((c) => c.alias.includes('/')).map((c) => (
              <button key={c.alias} onClick={() => cambiarCarpeta(c.alias)}
                style={{ width: '100%', padding: '7px 12px', textAlign: 'left',
                         background: carpeta === c.alias ? 'rgba(201,168,76,0.13)' : 'transparent',
                         color: carpeta === c.alias ? ORO : '#64748b', border: 'none',
                         borderRadius: 6, cursor: 'pointer', fontSize: 12.5 }}>
                {c.nombre}
              </button>
            ))}
          </div>
        )}

        {!esMovil && (
          <button onClick={() => setVerPlantilla(true)}
            style={{ width: '100%', marginTop: 16, padding: '7px 12px', background: 'transparent',
                     color: '#64748b', border: '1px solid #1e293b', borderRadius: 7,
                     cursor: 'pointer', fontSize: 11.5, display: 'flex', alignItems: 'center',
                     justifyContent: 'center', gap: 6 }}>
            <Eye size={12} /> Ver la plantilla
          </button>
        )}
      </div>

      {/* ── Lista ────────────────────────────────────────────────────────── */}
      <div style={{ display: mostrarLista ? 'block' : 'none',
                    width: esMovil ? '100%' : abierto ? 330 : '100%',
                    flexShrink: 0,
                    borderRight: !esMovil && abierto ? '1px solid #1e293b' : 'none',
                    paddingRight: !esMovil && abierto ? 14 : 0, transition: 'width .2s' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#475569' }} />
            <input ref={busquedaRef} value={buscando}
              onChange={(e) => setBuscando(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && cargarMensajes(carpeta, buscando)}
              placeholder="Buscar por asunto o remitente…"
              style={{ width: '100%', padding: '8px 10px 8px 31px', background: '#0f172a',
                       border: '1px solid #1e293b', borderRadius: 7, color: '#e2e8f0', fontSize: 12.5 }} />
          </div>
          <button onClick={() => { setBuscando(''); cargarMensajes(carpeta); cargarCarpetas(); }}
            title="Actualizar"
            style={{ padding: '8px 11px', background: '#0f172a', border: '1px solid #1e293b',
                     borderRadius: 7, color: '#94a3b8', cursor: 'pointer' }}>
            <RefreshCw size={14} className={cargando ? 'animate-spin' : ''} />
          </button>
        </div>

        {cargando ? (
          <p style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 36 }}>Cargando el buzón…</p>
        ) : mensajes.length === 0 ? (
          <p style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 36 }}>
            {buscando ? 'Ningún correo coincide' : 'No hay correos en esta carpeta'}
          </p>
        ) : mensajes.map((m) => {
          const persona = m.de?.[0] || m.para?.[0] || {};
          const activo = abierto?.uid === m.uid;
          return (
            <motion.div key={m.uid} onClick={() => abrir(m)}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              style={{ padding: '11px 12px', marginBottom: 4, borderRadius: 9, cursor: 'pointer',
                       background: activo ? 'rgba(201,168,76,0.1)' : m.leido ? 'transparent' : 'rgba(255,255,255,0.028)',
                       borderLeft: `2px solid ${activo ? ORO : m.leido ? 'transparent' : ORO}`,
                       display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                            background: m.leido ? '#1e293b' : ORO, color: m.leido ? '#94a3b8' : '#0a0a0a',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700 }}>
                {iniciales(persona.nombre, persona.email)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: m.leido ? 400 : 700,
                                 color: m.leido ? '#94a3b8' : '#e2e8f0', overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {persona.nombre || persona.email || '(sin remitente)'}
                  </span>
                  <span style={{ fontSize: 11, color: '#475569', flexShrink: 0 }}>{cuando(m.fecha)}</span>
                </div>
                <p style={{ margin: '2px 0 0', fontSize: 12.5,
                            fontWeight: m.leido ? 400 : 600,
                            color: m.leido ? '#64748b' : '#cbd5e1',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.asunto}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Lectura ──────────────────────────────────────────────────────── */}
      {mostrarLectura && (
        <motion.div initial={{ opacity: 0, x: esMovil ? 0 : 12 }} animate={{ opacity: 1, x: 0 }}
          style={{ flex: 1, minWidth: 0, width: esMovil ? '100%' : 'auto' }}>

          {esMovil && (
            <button onClick={() => setAbierto(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10,
                       padding: '7px 12px', background: 'rgba(255,255,255,0.05)', border: 'none',
                       borderRadius: 8, color: ORO, fontSize: 12.5, cursor: 'pointer' }}>
              <ChevronLeft size={15} /> Volver a la bandeja
            </button>
          )}

          <div style={{ display: 'flex', flexDirection: esMovil ? 'column' : 'row',
                        justifyContent: 'space-between', alignItems: esMovil ? 'stretch' : 'flex-start',
                        gap: 12, paddingBottom: 12, borderBottom: '1px solid #1e293b', marginBottom: 14 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: esMovil ? 15 : 16, color: '#e2e8f0',
                           lineHeight: 1.4, wordBreak: 'break-word' }}>{abierto.asunto}</h3>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                <b style={{ color: '#94a3b8' }}>{abierto.de?.[0]?.nombre}</b>{' '}
                &lt;{abierto.de?.[0]?.email}&gt; · {new Date(abierto.fecha).toLocaleString('es-ES')}
              </p>
              {abierto.para?.length > 0 && (
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#475569' }}>
                  para {abierto.para.map((p) => p.email).join(', ')}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => responder(abierto)} title="Responder"
                style={{ padding: '7px 12px', background: ORO, color: '#0a0a0a', border: 'none',
                         borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                         display: 'flex', alignItems: 'center', gap: 5 }}>
                <Reply size={13} /> Responder
              </button>
              <button onClick={() => mover(abierto, 'papelera')} title="A la papelera"
                style={{ padding: '7px 9px', background: 'transparent', border: '1px solid #1e293b',
                         borderRadius: 7, color: '#64748b', cursor: 'pointer' }}>
                <Trash2 size={13} />
              </button>
              {!esMovil && (
                <button onClick={() => setAbierto(null)} title="Cerrar"
                  style={{ padding: '7px 9px', background: 'transparent', border: '1px solid #1e293b',
                           borderRadius: 7, color: '#64748b', cursor: 'pointer' }}>
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          {abierto.adjuntos?.length > 0 && (
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
              {abierto.adjuntos.map((a, i) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5,
                                       padding: '5px 10px', background: '#0f172a', borderRadius: 6,
                                       color: '#94a3b8', border: '1px solid #1e293b' }}>
                  <Paperclip size={11} /> {a.nombre}
                  <span style={{ color: '#475569' }}>({Math.round(a.tamano / 1024)} KB)</span>
                </span>
              ))}
            </div>
          )}

          <div style={{ maxHeight: esMovil ? 'none' : 460, overflowY: esMovil ? 'visible' : 'auto',
                        padding: '4px 2px' }}>
            {abierto.html ? (
              // Los correos vienen maquetados con tablas de 600 px fijos y en el
              // móvil se salen. Se les añade un viewport y un tope de anchura
              // para que quepan sin obligar a arrastrar la pantalla a los lados.
              <iframe title="correo" sandbox=""
                srcDoc={`<meta name="viewport" content="width=device-width,initial-scale=1">
<style>html,body{margin:0;padding:0;max-width:100%;overflow-x:hidden;-webkit-text-size-adjust:100%}
img{max-width:100%!important;height:auto!important}
table{max-width:100%!important}
*{word-break:break-word}</style>${abierto.html}`}
                style={{ width: '100%', height: esMovil ? 520 : 440, border: 'none',
                         background: '#fff', borderRadius: 9 }} />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
                            fontSize: esMovil ? 13 : 13.5, lineHeight: 1.75, color: '#cbd5e1', margin: 0 }}>
                {abierto.texto || '(este correo no tiene contenido)'}
              </pre>
            )}
          </div>
        </motion.div>
      )}

      {/* ── Redactar ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {redactar && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setRedactar(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1000,
                     display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ y: 22, scale: 0.98 }} animate={{ y: 0, scale: 1 }}
              style={{ width: '100%', maxWidth: 660, background: '#0f172a', borderRadius: 14,
                       border: '1px solid #1e293b', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #1e293b',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 14.5, color: ORO }}>
                  {redactar.responder_a_uid ? 'Responder' : 'Nuevo correo'}
                </h3>
                <button onClick={() => setRedactar(null)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
                  <X size={17} />
                </button>
              </div>

              <div style={{ padding: 18 }}>
                {['para', 'asunto'].map((campo) => (
                  <input key={campo} value={redactar[campo] || ''}
                    onChange={(e) => setRedactar({ ...redactar, [campo]: e.target.value })}
                    placeholder={campo === 'para' ? 'Para (varios separados por comas)' : 'Asunto'}
                    style={{ width: '100%', padding: '10px 12px', marginBottom: 9, background: '#0a0f1c',
                             border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0', fontSize: 13 }} />
                ))}
                <textarea value={redactar.cuerpo || ''} rows={11}
                  onChange={(e) => setRedactar({ ...redactar, cuerpo: e.target.value })}
                  placeholder="Escribe el mensaje…"
                  style={{ width: '100%', padding: '11px 12px', background: '#0a0f1c',
                           border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0',
                           fontSize: 13.5, lineHeight: 1.65, resize: 'vertical', fontFamily: 'inherit' }} />

                <div style={{ display: 'flex', gap: 9, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                                  color: '#94a3b8', cursor: 'pointer' }}>
                    <input type="checkbox" checked={redactar.con_plantilla !== false}
                      onChange={(e) => setRedactar({ ...redactar, con_plantilla: e.target.checked })} />
                    Usar la plantilla de eHome
                  </label>
                  <input value={redactar.firmante || ''} placeholder="Firmar como…"
                    onChange={(e) => setRedactar({ ...redactar, firmante: e.target.value })}
                    style={{ flex: 1, minWidth: 130, padding: '7px 10px', background: '#0a0f1c',
                             border: '1px solid #1e293b', borderRadius: 7, color: '#e2e8f0', fontSize: 12 }} />
                </div>
              </div>

              <div style={{ padding: '13px 18px', borderTop: '1px solid #1e293b',
                            display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
                <button onClick={guardarBorrador}
                  style={{ padding: '9px 15px', background: 'transparent', border: '1px solid #1e293b',
                           borderRadius: 8, color: '#94a3b8', cursor: 'pointer', fontSize: 12.5 }}>
                  Guardar borrador
                </button>
                <button onClick={enviar} disabled={enviando}
                  style={{ padding: '9px 21px', background: ORO, color: '#0a0a0a', border: 'none',
                           borderRadius: 8, fontWeight: 700, fontSize: 12.5,
                           cursor: enviando ? 'default' : 'pointer', opacity: enviando ? 0.6 : 1,
                           display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Send size={13} /> {enviando ? 'Enviando…' : 'Enviar'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Vista previa de la plantilla ─────────────────────────────────── */}
      <AnimatePresence>
        {verPlantilla && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setVerPlantilla(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
                     display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ width: '100%', maxWidth: 640, background: '#0f172a', borderRadius: 14,
                          border: '1px solid #1e293b', overflow: 'hidden' }}>
              <div style={{ padding: '13px 18px', borderBottom: '1px solid #1e293b',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 14, color: ORO }}>Así salen los correos de eHome</h3>
                <button onClick={() => setVerPlantilla(false)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
                  <X size={17} />
                </button>
              </div>
              <iframe title="plantilla" src={`${API}/mail/plantilla/vista-previa`}
                style={{ width: '100%', height: 560, border: 'none' }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
